import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
	Card,
	Box,
	NavLink,
	Stack,
	Group,
	ActionIcon,
	Menu,
	MediaQuery,
	TextInput,
	Text,
	ScrollArea,
	Collapse,
	UnstyledButton,
} from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { Icons } from '../../utils/Icons';

const SIDEBAR_WIDTH = 250;
const EMPTY_ARRAY = [];

const MasterLayout = ({
	moduleTitle,
	masters = EMPTY_ARRAY,
	categories = EMPTY_ARRAY,
	breadcrumbLinks,
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [searchQuery, setSearchQuery] = useState('');
	const [openedCategories, setOpenedCategories] = useState([]);
	const [activeEntityId, setActiveEntityId] = useState(
		() => searchParams.get('entity') || null
	);

	// Flatten categories into masters list
	const allMasters = useMemo(
		() => (categories.length > 0 ? categories.flatMap(cat => cat.masters) : masters),
		[categories, masters]
	);

	// Initialize all categories as open by default
	useEffect(() => {
		if (categories.length > 0 && openedCategories.length === 0) {
			setOpenedCategories(categories.map(c => c.id));
		}
	}, [categories, openedCategories.length]);

	const activeMaster = useMemo(
		() => allMasters.find(m => m.id === activeEntityId) || allMasters[0],
		[allMasters, activeEntityId]
	);

	// Filter categories by search query
	const filteredCategories = useMemo(() => {
		if (!searchQuery.trim()) return categories;

		const query = searchQuery.toLowerCase();
		return categories
			.map(category => ({
				...category,
				masters: category.masters.filter(master => {
					const labelMatch = master.label.toLowerCase().includes(query);
					const keywordsMatch = master.searchKeywords?.some(kw =>
						kw.toLowerCase().includes(query)
					);
					return labelMatch || keywordsMatch;
				})
			}))
			.filter(category => category.masters.length > 0);
	}, [categories, searchQuery]);

	const handleEntityChange = useCallback(
		entityId => {
			setActiveEntityId(entityId);
			setSearchParams({ entity: entityId });
		},
		[setSearchParams]
	);

	const toggleCategory = useCallback(categoryId => {
		setOpenedCategories(prev =>
			prev.includes(categoryId)
				? prev.filter(id => id !== categoryId)
				: [...prev, categoryId]
		);
	}, []);

	const ActiveComponent = useMemo(() => activeMaster?.component, [activeMaster]);

	return (
		<div style={{ width: '100%' }}>
			<Stack spacing="sm">
				<Group position="apart" align="center">
					{/* Mobile Menu */}
					<MediaQuery largerThan="sm" styles={{ display: 'none' }}>
						<Menu shadow="md" width={SIDEBAR_WIDTH}>
							<Menu.Target>
								<ActionIcon size="lg" variant="subtle">
									<i className={`${Icons.menu} fs-20`}></i>
								</ActionIcon>
							</Menu.Target>

							<Menu.Dropdown>
								<Menu.Label>Maestros</Menu.Label>
								<ScrollArea style={{ maxHeight: '70vh' }} type="auto">
									{filteredCategories.length > 0 ? (
										filteredCategories.map(category => {
											const categoryIconClass = category.icon;
											return (
												<div key={category.id}>
													<Menu.Label>
														<Group spacing={4}>
															{categoryIconClass && <i className={categoryIconClass} style={{ fontSize: '14px' }}></i>}
															<span style={{ fontSize: '11px' }}>{category.label}</span>
														</Group>
													</Menu.Label>
													{category.masters.map(master => {
														const iconClass = master.icon;
														return (
															<Menu.Item
																key={master.id}
																icon={<i className={iconClass} style={{ fontSize: '16px' }}></i>}
																onClick={() => handleEntityChange(master.id)}
																color={master.id === activeMaster?.id ? 'orange' : undefined}
															>
																<span style={{ fontSize: '12px' }}>{master.label}</span>
															</Menu.Item>
														);
													})}
												</div>
											);
										})
									) : (
										allMasters.map(master => {
											const iconClass = master.icon;
											return (
												<Menu.Item
													key={master.id}
													icon={<i className={iconClass} style={{ fontSize: '16px' }}></i>}
													onClick={() => handleEntityChange(master.id)}
													color={master.id === activeMaster?.id ? 'orange' : undefined}
												>
													<span style={{ fontSize: '12px' }}>{master.label}</span>
												</Menu.Item>
											);
										})
									)}
								</ScrollArea>
							</Menu.Dropdown>
						</Menu>
					</MediaQuery>

					{/* Breadcrumb placeholder */}
					<Box style={{ flex: 1 }}>
						{/* BreadCrumb component will be rendered in parent page */}
					</Box>
				</Group>

				<Group align="flex-start" spacing="md" style={{ width: '100%' }}>
					{/* Desktop Sidebar */}
					<MediaQuery smallerThan="sm" styles={{ display: 'none' }}>
						<Card
							withBorder
							shadow="sm"
							radius="md"
							p="md"
							style={{
								width: SIDEBAR_WIDTH,
								flexShrink: 0,
								minHeight: 'calc(100vh - 250px)',
								alignSelf: 'stretch'
							}}
						>
							<Stack spacing="sm">
								<TextInput
									placeholder="Buscar maestro..."
									icon={<i className={Icons.search}></i>}
									value={searchQuery}
									onChange={e => setSearchQuery(e.currentTarget.value)}
									size="xs"
									styles={{
										input: { fontSize: '12px' }
									}}
								/>

								<ScrollArea style={{ height: 'calc(100vh - 340px)' }} type="auto">
									{categories.length > 0 ? (
										<Stack spacing="xs">
											{filteredCategories.map(category => {
												const isOpened = openedCategories.includes(category.id);
												const categoryIconClass = category.icon;
												return (
													<Box key={category.id}>
														<UnstyledButton
															onClick={() => toggleCategory(category.id)}
															style={{
																width: '100%',
																padding: '8px 12px',
																borderRadius: 4,
																display: 'flex',
																alignItems: 'center',
																gap: 8,
																transition: 'background-color 0.2s',
																cursor: 'pointer',
																backgroundColor: 'transparent'
															}}
															onMouseEnter={e => {
																e.currentTarget.style.backgroundColor = '#f8f9fa';
															}}
															onMouseLeave={e => {
																e.currentTarget.style.backgroundColor = 'transparent';
															}}
														>
															{categoryIconClass && <i className={categoryIconClass} style={{ fontSize: '14px' }}></i>}
															<Text size="xs" style={{ flex: 1 }}>
																{category.label}
															</Text>
															<i
																className={Icons.chevronDown}
																style={{
																	transition: 'transform 0.2s',
																	transform: isOpened ? 'rotate(180deg)' : 'rotate(0deg)'
																}}
															></i>
														</UnstyledButton>

														<Collapse in={isOpened}>
															<Stack spacing={2} mt={4}>
																{category.masters.map(master => {
																	const iconClass = master.icon;
																	return (
																		<NavLink
																			key={master.id}
																			label={<span style={{ fontSize: '12px' }}>{master.label}</span>}
																			icon={<i className={iconClass} style={{ fontSize: '14px' }}></i>}
																			color="secondary"
																			variant="subtle"
																			active={master.id === activeMaster?.id}
																			onClick={() => handleEntityChange(master.id)}
																			styles={theme => ({
																				root: {
																					borderRadius: theme.radius.sm,
																					fontWeight: master.id === activeMaster?.id ? 600 : 400,
																					padding: '6px 12px'
																				}
																			})}
																		/>
																	);
																})}
															</Stack>
														</Collapse>
													</Box>
												);
											})}
										</Stack>
									) : (
										<Stack spacing="xs">
											{allMasters.map(master => {
												const iconClass = master.icon;
												return (
													<NavLink
														key={master.id}
														label={<span style={{ fontSize: '12px' }}>{master.label}</span>}
														icon={<i className={iconClass} style={{ fontSize: '16px' }}></i>}
														color="secondary"
														variant="subtle"
														active={master.id === activeMaster?.id}
														onClick={() => handleEntityChange(master.id)}
														styles={theme => ({
															root: {
																borderRadius: theme.radius.sm,
																fontWeight: master.id === activeMaster?.id ? 600 : 400,
															}
														})}
													/>
												);
											})}
										</Stack>
									)}
								</ScrollArea>

								{searchQuery && filteredCategories.length === 0 && (
									<Text size="xs" color="dimmed" align="center" py="md">
										No se encontraron maestros
									</Text>
								)}
							</Stack>
						</Card>
					</MediaQuery>

					{/* Main Content */}
					<Box style={{ flex: 1 }}>
						{ActiveComponent ? (
							<ActiveComponent key={activeMaster?.id} />
						) : (
							<Card>
								<Text>Por favor selecciona un maestro</Text>
							</Card>
						)}
					</Box>
				</Group>
			</Stack>
		</div>
	);
};

export default MasterLayout;
